import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import format from 'date-fns/format';
import {
  CreateBookingInput,
  HotelFragmentFragment,
  Mutation,
  MutationCreateBookingArgs,
} from '../../../../generated/types';
import { CalendarIcon, PhoneIcon } from '@chakra-ui/icons';
import { useMutation } from '@apollo/client';
import { CREATE_BOOKING_QUERY } from '../../../../queries';
import { useStore } from '../../../../store/provider';
import 'react-datepicker/dist/react-datepicker.css';

interface BookingFormValues {
  firstname: string;
  lastname: string;
  street: string;
  postcode: string;
  city: string;
  email: string;
  phone: string;
  rooms_count: string;
  beds: string;
  start_date: Date;
  end_date: Date;
  body: string;
}

interface BookingFormProps {
  hotelId: number;
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
  inline?: boolean;
}

export default function BookingForm({
  hotelId,
  isOpen,
  onClose,
  onSubmitted,
  inline,
}: BookingFormProps) {
  // const toast = useToast();
  const [createBooking, { loading }] = useMutation<
    Mutation,
    MutationCreateBookingArgs
  >(CREATE_BOOKING_QUERY);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    setError,
  } = useForm<BookingFormValues>();
  const start_date = watch('start_date');
  const end_date = watch('end_date');
  const store = useStore();
  const toast = useToast();

  const onSubmit = async (values: BookingFormValues) => {
    const apiValues: CreateBookingInput = {
      hotel: hotelId + '',
      firstname: values.firstname,
      lastname: values.lastname,
      street: values.street,
      postcode: values.postcode,
      city: values.city,
      email: values.email,
      phone: values.phone,
      date_range: {
        value: format(values.start_date, 'yyyy-MM-dd'),
        end_value: format(values.end_date, 'yyyy-MM-dd'),
      },
      rooms_count: values.rooms_count,
      beds: values.beds,
      body: values.body,
      frontend: store.config.frontendName,
    };

    const result = await createBooking({
      variables: {
        data: apiValues,
      },
    });

    if (result.data) {
      if (result.data.createBooking && result.data.createBooking.created) {
        toast({
          title: 'Buchungsanfrage erstellt',
          description:
            'Deine Buchungsanfrage wurde erstellt. Das Hotel wird sich in Kürze bei Dir melden. Vielen Dank!',
          status: 'success',
          duration: 6000,
          isClosable: true,
        });

        onSubmitted && onSubmitted();

        reset();
        onClose();
        return;
      } else {
        if (result.data.createBooking?.errors) {
          result.data.createBooking?.errors.forEach((error) => {
            setError(error.field as any, error);
          });
        }
      }
    }

    toast({
      title: 'Fehler',
      description:
        'Bei der Buchungsanfrage ist ein Fehler aufgetreten. Bitte versuche es später erneut.',
      status: 'error',
      duration: 6000,
      isClosable: true,
    });
  };

  useEffect(() => {
    register('start_date');
    register('end_date');
  }, []);

  const FormFields = () => {
    return (
      <>
        <Flex marginTop="4">
          <FormControl flex="1" isRequired>
            <FormLabel fontSize="sm" htmlFor="firstname">
              Vorname
            </FormLabel>
            <Input
              variant="outline"
              size="sm"
              type="text"
              id="firstname"
              name="firstname"
              {...register('firstname', {
                required: true,
              })}
            />
          </FormControl>
          <FormControl flex="1" isRequired marginLeft="2">
            <FormLabel fontSize="sm" htmlFor="lastname">
              Nachname
            </FormLabel>
            <Input
              size="sm"
              type="text"
              id="lastname"
              name="lastname"
              {...register('lastname', {
                required: true,
              })}
            />
          </FormControl>
        </Flex>

        <FormControl marginTop="4">
          <FormLabel fontSize="sm" htmlFor="street">
            Straße
          </FormLabel>
          <Input
            name="street"
            size="sm"
            type="text"
            id="street"
            {...register('street')}
          />
        </FormControl>

        <Flex marginTop="4">
          <FormControl flex="1">
            <FormLabel fontSize="sm" htmlFor="postcode">
              PLZ
            </FormLabel>
            <Input
              name="postcode"
              size="sm"
              type="text"
              id="postcode"
              {...register('postcode')}
            />
          </FormControl>

          <FormControl flex="1" marginLeft="2">
            <FormLabel fontSize="sm" htmlFor="city">
              Ort
            </FormLabel>
            <Input
              name="city"
              size="sm"
              type="text"
              id="city"
              {...register('city')}
            />
          </FormControl>
        </Flex>

        <Flex marginTop="4">
          <FormControl flex="1" isRequired>
            <FormLabel fontSize="sm" htmlFor="email">
              E-Mail
            </FormLabel>
            <InputGroup size="sm">
              <InputLeftElement
                pointerEvents="none"
                children={<CalendarIcon color="gray.300" />}
              />
              <Input
                type="email"
                id="email"
                name="email"
                {...register('email', {
                  required: true,
                })}
              />
            </InputGroup>
          </FormControl>

          <FormControl flex="1" marginLeft="2">
            <FormLabel fontSize="sm" htmlFor="phone">
              Telefon
            </FormLabel>
            <InputGroup size="sm">
              <InputLeftElement
                pointerEvents="none"
                children={<PhoneIcon color="gray.300" />}
              />
              <Input
                type="text"
                id="phone"
                name="phone"
                {...register('phone')}
              />
            </InputGroup>
          </FormControl>
        </Flex>

        <Flex marginTop="4">
          <FormControl flex="1" isRequired>
            <FormLabel fontSize="sm" htmlFor="rooms_count">
              Anzahl Zimmer
            </FormLabel>
            <Input
              size="sm"
              type="number"
              id="rooms_count"
              name="rooms_count"
              {...register('rooms_count', {
                required: true,
              })}
            />
          </FormControl>

          <FormControl flex="1" isRequired marginLeft="2">
            <FormLabel fontSize="sm" htmlFor="beds">
              Anzahl Betten
            </FormLabel>
            <Input
              size="sm"
              type="number"
              id="beds"
              name="beds"
              {...register('beds', {
                required: true,
              })}
            />
          </FormControl>
        </Flex>

        <Flex marginTop="4">
          <FormControl flex="1" isRequired>
            <FormLabel fontSize="sm" htmlFor="firstname">
              Von (Datum)
            </FormLabel>

            <DatePicker
              selected={start_date}
              selectsStart
              startDate={start_date}
              endDate={end_date}
              onChange={(date) => {
                if (date && !Array.isArray(date)) {
                  setValue('start_date', date);
                }
              }}
              minDate={new Date()}
              customInput={
                <InputGroup size="sm">
                  <InputLeftElement
                    pointerEvents="none"
                    children={<CalendarIcon color="gray.300" />}
                  />
                  <Input
                    type="text"
                    id="start_date"
                    name="start_date"
                    readOnly
                    value={start_date ? format(start_date, 'dd.MM.yyyy') : ''}
                  />
                </InputGroup>
              }
              dateFormat="dd.MM.yyyy"
            />
          </FormControl>

          <FormControl flex="1" isRequired marginLeft="2">
            <FormLabel fontSize="sm" htmlFor="firstname">
              Bis (Datum)
            </FormLabel>
            <DatePicker
              selected={end_date}
              selectsEnd
              startDate={start_date}
              endDate={end_date}
              onChange={(date) => {
                if (date && !Array.isArray(date)) {
                  setValue('end_date', date);
                }
              }}
              minDate={start_date || new Date()}
              customInput={
                <InputGroup size="sm">
                  <InputLeftElement
                    pointerEvents="none"
                    children={<CalendarIcon color="gray.300" />}
                  />
                  <Input
                    type="text"
                    id="end_date"
                    name="end_date"
                    readOnly
                    value={end_date ? format(end_date, 'dd.MM.yyyy') : ''}
                  />
                </InputGroup>
              }
              dateFormat="dd.MM.yyyy"
            />
          </FormControl>
        </Flex>
        <FormControl marginTop={4} flex="1">
          <FormLabel fontSize="sm" htmlFor="body">
            Nachricht
          </FormLabel>
          <Textarea size="sm" id="body" name="body" {...register('body')} />
        </FormControl>
      </>
    );
  };

  if (inline) {
    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormFields />
        <Box mt={4}>
          <Button
            disabled={loading}
            isLoading={loading}
            width="100%"
            variant="outline"
            type="submit"
            colorScheme="blue"
          >
            Buchungsanfrage abschicken
          </Button>
        </Box>
      </form>
    );
  }

  return (
    <Modal
      closeOnOverlayClick={false}
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay zIndex={1100}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalContent>
            <ModalHeader>Buchungsanfrage</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormFields />
            </ModalBody>

            <ModalFooter>
              <Flex marginTop="4" justifyContent="flex-end">
                <Button
                  disabled={loading}
                  isLoading={loading}
                  width="100%"
                  variant="outline"
                  type="submit"
                  colorScheme="blue"
                >
                  Buchungsanfrage abschicken
                </Button>
              </Flex>
            </ModalFooter>
          </ModalContent>
        </form>
      </ModalOverlay>
    </Modal>
  );
}
